import React, { useState, useEffect,useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { ArrowBack, ArrowForward, Sort as SortIcon } from "@mui/icons-material";
import Sort from "./Sort.mp4"; // Adjust path if necessary

const SortBySection = ({ onSortChange }) => {
  const [sortCriterion, setSortCriterion] = useState("marketCap");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width:600px)");
  const [currentPage, setCurrentPage] = useState(0); // Track the current page
  const itemsPerPage  = 8; // Number of visible buttons at a time
  const buttons = [
    { label: "Market Capitalization", value: "marketCap" },
    { label: "Points", value: "rank" },
    { label: "Strong Buy", value: "strongBuy" },
    { label: "Buy", value: "buy" },
    { label: "Undervalued Stocks", value: "undervalues_stocks" },
    { label: "Total Stock Volume Traded", value: "volume" },
    { label: "Call Put Volume Ratio", value: "call_put_ratio" },
    { label: "Call Put Open Interest Ratio", value: "call_put_open_ratio" },
    { label: "Total Call Open", value: "total_call_openinterest" },
    { label: "Total Call Volume", value: "total_call_volume" },
    { label: "Total Revenue Growth", value: "revenue_growth" },
    { label: "Total Earning Growth", value: "earnings_growth" },
    { label: "Dividend Yield", value: "dividend_yield" },
    { label: "Free Cash Flow", value: "free_cash_flow" },
    { label: "Forward P/E Ratio", value: "forward_pe" },
    { label: "Forward EPS", value: "forwardEps" },


    
  ];

  const totalPages = Math.ceil(buttons.length / itemsPerPage);

  const handleArrowClick = (direction) => {
    setCurrentPage((prev) => {
      if (direction === "left") {
        return prev > 0 ? prev - 1 : 0;
      } else {
        return prev < totalPages - 1 ? prev + 1 : totalPages - 1;
      }
    });
  };

  const handleButtonClick = (value) => {
    setSortCriterion(value);
    if (onSortChange) {
      onSortChange(value);
    }
  };

  // Calculate visible items for the current page
  const visibleButtons = buttons.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  const handleSelect = (value) => {
    setSortCriterion(value);
    setIsDrawerOpen(false);
  };



  useEffect(() => {
    if (onSortChange) {
      onSortChange(sortCriterion);
    }
  }, [sortCriterion, onSortChange]);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "25vh",
        overflow: "hidden",
        backgroundColor: "#000",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          opacity: 0.7,
        }}
      >
        <source src={Sort} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      <Box
        sx={{
          position: "absolute",
          top: "0",
          left: "0",
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        }}
      />

      <Typography
        variant="h4"
        sx={{
          zIndex: 1,
          color: "#fff",
          fontWeight: "bold",
          fontFamily: "'Work Sans', sans-serif",
          letterSpacing: "2px",
          marginBottom: "16px",
        }}
      >
        <SortIcon
          sx={{
            fontSize: "2rem",
            verticalAlign: "middle",
            marginRight: "8px",
          }}
        />
        Sort Market
      </Typography>

      {isMobile ? (
        // Mobile View
        <>
          <Button
            variant="outlined"
            onClick={() => setIsDrawerOpen(true)}
            sx={{
              zIndex: 1,
              color: "#fff",
              borderColor: "#fff",
              fontSize: "14px",
              fontWeight: "bold",
              fontFamily: "'Work Sans', sans-serif",
              padding: "10px 20px",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
              },
            }}
          >
            {buttons.find((btn) => btn.value === sortCriterion)?.label || "Select Option"}
          </Button>

          <Drawer
            anchor="bottom"
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            sx={{
              "& .MuiDrawer-paper": {
                backgroundColor: "#000",
                color: "#fff",
              },
            }}
          >
            <Box sx={{ padding: 2 }}>
              <Typography
                variant="h6"
                sx={{
                  marginBottom: 2,
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                Select Sorting Option
              </Typography>
              <List>
                {buttons.map((button) => (
                  <ListItem key={button.value} disablePadding>
                    <ListItemButton
                      onClick={() => handleSelect(button.value)}
                      sx={{
                        textAlign: "center",
                        color: "#fff",
                        fontWeight: "bold",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.1)",
                        },
                      }}
                    >
                      <ListItemText primary={button.label} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Drawer>
        </>
      ) : (
        // Desktop View with Carousel
        <Box
        sx={{
          zIndex: 1,
          width: "90%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left Arrow */}
        <IconButton
          onClick={() => handleArrowClick("left")}
          sx={{
            color: "#fff",
            fontSize: "24px",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
          }}
          disabled={currentPage === 0}
        >
          <ArrowBack />
        </IconButton>

        {/* Grid of Buttons */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)", // 4 items per row
            gridTemplateRows: "repeat(2, auto)", // 2 rows
            gap: 2,
            width: "80%",
          }}
        >
          {visibleButtons.map((button) => (
            <Button
              key={button.value}
              onClick={() => handleButtonClick(button.value)}
              sx={{
                fontSize: "14px",
                fontWeight: "bold",
                fontFamily: "'Work Sans', sans-serif",
                letterSpacing: "1px",
                padding: "10px",
                color: "#fff",
                borderColor: "#fff",
                backgroundColor:
                  sortCriterion === button.value
                    ? "rgba(255, 255, 255, 0.5)"
                    : "transparent",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  borderColor: "#fff",
                },
                textTransform: "none",
              }}
              variant="outlined"
            >
              {button.label}
            </Button>
          ))}
        </Box>

        {/* Right Arrow */}
        <IconButton
          onClick={() => handleArrowClick("right")}
          sx={{
            color: "#fff",
            fontSize: "24px",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
            },
          }}
          disabled={currentPage === totalPages - 1}
        >
          <ArrowForward />
        </IconButton>
      </Box>
      )}
    </Box>
  );
};

export default SortBySection;
